import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useDeepCompareEffect } from 'react-use'

import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'

import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator'

import { zodResolver } from '@hookform/resolvers/zod'
import { FormProvider, useForm } from 'react-hook-form'
import { Appointment, Options, Requisition } from 'models'
import {
	ExtendedNonSurgicalAdmissionSchema,
	NonSurgicalAdmissionSchema,
} from './NonSurgicalAdmissionSchema'

import FinalizeTaskDialog from '../FinalizeTaskController/FinalizeTaskDialog'

import styles from '../Dialog.module.sass'
import useSubmitTask from 'hooks/useSubmitTask'
import useSession from 'hooks/useSession'
import { NonSurgicalAdmissionForm } from './NonSurgicalAdmissionForm'
import TaskHeaderController from 'controllers/TaskManager/TaskSubControllers/TaskHeader/TaskHeader'
import TaskNotesController from 'controllers/TaskManager/TaskSubControllers/TaskNotesController/TaskNotesController'
import AlertBox from 'components/AlertBox/AlertBox'
import InfoList from 'components/InfoList/InfoList'
import { useNoWaitFetchList } from 'store'
import TaskMenuController from '../TaskMenu/TaskMenuController'
import { getNextAppointment } from '../utils'
import { mapSurgeryClinicalApprovalRequestDetails } from 'controllers/TaskManager/TaskSubControllers/TaskDetailsControllers/detailsUtils'
import { NonSurgicalAdmissionAdministrativeForm } from './NonSurgicalAdmissionAdministrativeForm'
import TaskRefusalController from 'controllers/TaskManager/TaskSubControllers/TaskRefusalController/TaskRefusalController'
import PersonalAccidentWarning from 'controllers/TaskManager/TaskSubControllers/PersonalAccidentWarning'

const NonSurgicalAdmissionTaskController = (props) => {
	const { i18n } = useGlobal()
	const { data, resolve, dismiss, id } = props
	const [defaultValues, setDefaultValues] = useState(null)
	const [argValues, setArgValues] = useState(null)
	const [submissionType, setSubmissionType] = useState(null)
	const history = useHistory()
	const { user: sessionUser } = useSession()

	const IS_ADMINISTRATIVE = sessionUser?.roles.includes('manager')

	const schema = IS_ADMINISTRATIVE
		? ExtendedNonSurgicalAdmissionSchema({ ...argValues })
		: NonSurgicalAdmissionSchema({ ...argValues })

	const methods = useForm({
		mode: 'onSubmit',
		resolver: zodResolver(schema),
	})
	const { handleSubmit, formState, watch, reset, getValues } = methods

	const [outsideNetwork] = watch(['outsideNetwork'])

	const {
		error,
		submitting,
		resolveTask,
		approveTask,
		requestApprovalTask,
		requestManagerApprovalTask,
	} = useSubmitTask(data?.id, formState?.errors)

	const { contents } = useNoWaitFetchList(
		[
			`clinical_processes/${data?.processId}/appointments`,
			`clinical_processes/${data?.processId}/requisitions/${data?.requisitionId}/notes`,
			`clinical_processes/${data.processId}/requisitions/${data.requisitionId}`,
			IS_ADMINISTRATIVE && outsideNetwork && `metadata/districts`,
			IS_ADMINISTRATIVE && !outsideNetwork
				? argValues?.specialtyId
					? `clinical_processes/${data.processId}/requisitions/${data.requisitionId}/specialties/${argValues.specialtyId}/service_providers`
					: `clinical_processes/${data.processId}/requisitions/${data.requisitionId}/service_providers`
				: null,
		],
		[
			Appointment.fromJSON,
			(json) => {
				return json.notes
			},
			Requisition.fromJSON,
			IS_ADMINISTRATIVE && outsideNetwork && Options.fromSimpleObjectJSON,
			IS_ADMINISTRATIVE &&
				!outsideNetwork &&
				Options.fromSimpleObjectJSON,
		]
	)

	const [
		appointmentsContents,
		notesContents,
		requisitionContents,
		districtsContents,
		serviceProvidersContents,
	] = contents

	const { contents: appointments } =
		appointmentsContents && appointmentsContents.state === 'hasValue'
			? appointmentsContents
			: { state: appointmentsContents?.state }

	const { contents: notes, state: notesState } =
		notesContents && notesContents.state === 'hasValue'
			? notesContents
			: { state: notesContents?.state }

	const { contents: requisition, state: requisitionState } =
		requisitionContents && requisitionContents.state === 'hasValue'
			? requisitionContents
			: { state: requisitionContents?.state }

	const { contents: districts } =
		districtsContents && districtsContents.state === 'hasValue'
			? districtsContents
			: { state: districtsContents?.state }

	const { contents: serviceProviders } =
		serviceProvidersContents &&
		serviceProvidersContents.state === 'hasValue'
			? serviceProvidersContents
			: { state: serviceProvidersContents?.state }

	const isLoading = requisitionState === 'loading'
	const isNotesLoading = notesState === 'loading'

	useEffect(() => {
		if (
			!isLoading &&
			(data.formState || defaultValues) &&
			!formState.isDirty &&
			formState.submitCount === 0
		) {
			reset &&
				reset(
					data.formState
						? {
								...data.formState,
								...(defaultValues
									? { exams: defaultValues.exams }
									: {}),
						  }
						: defaultValues
				)
		}
	}, [
		reset,
		defaultValues,
		isLoading,
		data.formState,
		formState.isDirty,
		formState.submitCount,
	])

	useDeepCompareEffect(() => {
		if (!isLoading) {
			let defaultValues = {}
			if (requisition) {
				const requestedExams = (requisition?.exams || []).map(
					(item) => {
						return {
							id: item.id,
							checked: !item.refuse,
						}
					}
				)

				defaultValues = {
					...defaultValues,
					exams: requestedExams,
				}

				if (
					requisition.providerDescription &&
					requisition.district?.id &&
					IS_ADMINISTRATIVE
				) {
					defaultValues = {
						...defaultValues,
						'externalProvider.name':
							requisition.providerDescription,
						'externalProvider.districtId': requisition.district?.id,
					}
				}

				setArgValues({
					requestEscort: requisition?.requestEscort,
					requiresChargeAmount: data?.requiresChargeAmount,
					specialtyId: requisition?.specialty?.id,
				})
			}

			if (requisition?.requestEscort && IS_ADMINISTRATIVE) {
				defaultValues = {
					...defaultValues,
					'needsOptions.housing': false,
					'needsOptions.transport': false,
				}
			}

			if (IS_ADMINISTRATIVE) {
				defaultValues = {
					...defaultValues,
					notes: requisition?.notes || '',
					outsideNetwork: false,
					administrativeObservation:
						requisition?.administrativeObservation || '',
				}
			}
			if (requisition?.requestEscort) {
				defaultValues = {
					...defaultValues,
					needEscort: false,
				}
			}

			if (serviceProviders) {
				const establishmentId =
					requisition?.establishment?.id.toString()
				const hasEstablishment = serviceProviders.some(
					(provider) => provider.value === establishmentId
				)

				if (hasEstablishment) {
					defaultValues = {
						providerId: establishmentId,
						...defaultValues,
					}
				}
			}

			setDefaultValues({ ...defaultValues })
		}
	}, [isLoading, requisition ?? {}, serviceProviders ?? []])

	const onSubmit = (formData) => {
		let finalData = {}

		const { exams, notes, externalProvider, providerId, ...remainingData } =
			formData

		finalData = { ...remainingData }

		if (exams) {
			const mappedExams = exams.map(({ id, checked }) => {
				return { id, approve: checked }
			})

			finalData = { exams: mappedExams, ...finalData }
		}

		finalData = outsideNetwork
			? { ...finalData, externalProvider }
			: { ...finalData, providerId }

		if (submissionType === 'resolve') {
			resolveTask(finalData)
				.then(() => resolve())
				.catch(() => {})
		} else if (submissionType === 'approve') {
			approveTask(finalData)
				.then(() => resolve())
				.catch(() => {})
		}
	}

	if (isLoading && !formState.isDirty) {
		return <LoadingIndicator />
	}

	const customError = error?.map((err) => {
		return {
			title: i18n`Error`,
			text: err,
		}
	})

	const nextAppointment = getNextAppointment(appointments)

	const details = mapSurgeryClinicalApprovalRequestDetails(
		requisition,
		nextAppointment,
		i18n
	)

	return (
		<>
			{submitting && (
				<FinalizeTaskDialog
					dismiss={dismiss}
					submitting={submitting}
					task={data}
				/>
			)}
			<FormProvider {...methods}>
				<div className={styles['form-container']}>
					<TaskHeaderController
						task={data}
						requisition={requisition}
					/>

					<div className={styles['notes-form-container']}>
						<TaskNotesController
							data={{
								id: data.id,
								processId: data.processId,
								taskNote: notes,
							}}
							requisition={requisition}
							isNotesLoading={isNotesLoading}
						/>
					</div>

					<form
						id={`form_${id}`}
						className={styles['form']}
						onSubmit={handleSubmit(onSubmit)}
					>
						{customError && customError.length > 0 && (
							<AlertBox
								className={styles['alert-box']}
								type='error'
								items={customError}
							/>
						)}

						<TaskRefusalController requisition={requisition} />

						<PersonalAccidentWarning task={data} />

						{IS_ADMINISTRATIVE &&
							requisition?.clinicalObservation && (
								<InfoList
									items={[
										{
											title: i18n`Clinical Observations`,
											text: requisition.clinicalObservation,
											icon: 'observation',
										},
									]}
								/>
							)}

						{IS_ADMINISTRATIVE && <InfoList items={details} />}

						{IS_ADMINISTRATIVE ? (
							<NonSurgicalAdmissionAdministrativeForm
								data={{
									requisition,
									requiresChargeAmount:
										data?.requiresChargeAmount,
									outsideNetwork: outsideNetwork,
									districts: districts,
									serviceProviders: serviceProviders,
									processBranch: data?.segment?.id,
									isFromIsland: data?.injured?.isFromIsland,
									processInjuredState:
										data.processInjuredStateId,
								}}
							/>
						) : (
							<NonSurgicalAdmissionForm
								data={{
									requisition,
									isFromIsland: data?.injured?.isFromIsland,
								}}
							/>
						)}
					</form>
				</div>
			</FormProvider>
			{IS_ADMINISTRATIVE ? (
				<TaskMenuController
					data={{
						formId: id,
						options: ['postpone', 'forward', 'refuse'],
						submitting,
						setSubmissionType,
						submissionType: 'resolve',
						requestApprovalTask,
						requestManagerApprovalTask,
						getValues,
					}}
				/>
			) : (
				<TaskMenuController
					data={{
						formId: id,
						options: ['postpone', 'forward', 'refuse'],
						history,
						submitting,
						setSubmissionType,
						submissionType: 'approve',
						getValues,
					}}
				/>
			)}
		</>
	)
}

export default NonSurgicalAdmissionTaskController
